var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

var _createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

import { autorun, computed, extendObservable, when } from 'mobx';
/* 
 * This is the public facade over all of the routing interface.
 * The `Router` is a coordinator of other objects in the system.
 */

import querystring from 'querystring';
import delay from './util/delay';
import createRouteStateTreeNode from './model/createRouteStateTreeNode';
import RouterStore from './model/RouterStore';
import Scheduler from './scheduling/Scheduler';
import { EventTypes } from './events';
import { NavigationTypes } from './model/Navigation';

var Router = function () {
  // This is computed from Scheduler event observable.
  // Private members
  function Router(history, config, getContext, middleware) {
    var _this = this;

    _classCallCheck(this, Router);

    this._handleNextNavigation = function () {
      var _nextNavigation = _this._nextNavigation;

      if (!_nextNavigation) {
        return;
      } // Do this on next tick so we don't clobber current event.
      // TODO: Move this redirect logic to a middleware.


      setTimeout(function () {
        switch (_nextNavigation.type) {
          case NavigationTypes.PUSH:
            return _this.push(_nextNavigation.to);

          case NavigationTypes.REPLACE:
            return _this.replace(_nextNavigation.to);

          case NavigationTypes.GO_BACK:
            return _this.goBack();

          default:
            throw new TypeError('Invalid navigation returned (' + _nextNavigation.type + ')');
        }
      });
    };

    this._handleLocationChange = function (location, action) {
      _this._scheduler.schedule(asNavigation(location, action));
    };

    this._disposers = [];
    this._history = history;
    var root = createRouteStateTreeNode({
      key: '@@ROOT',
      path: '',
      match: 'partial'
    }, getContext); // Initial root.

    this._initialChildren = config;
    this._store = new RouterStore(root);
    this._scheduler = new Scheduler(this._store, middleware);
    extendObservable(this, {
      location: computed(function () {
        return _this._store.location;
      }),
      activeRoutes: computed(function () {
        return _this._store.routes;
      }),
      activeRouteKeys: computed(function () {
        return _this.activeRoutes.map(function (r) {
          return r.node.value.key;
        });
      }),
      error: computed(function () {
        return _this._store.error;
      }),
      // Private usage to figure out if an event has a next navigation object.
      _nextNavigation: computed(function () {
        var event = _this._scheduler.event;
        return event !== null ? event.nextNavigation !== null ? event.nextNavigation : null : null;
      }),
      isNavigating: computed(function () {
        var type = _this._scheduler.event.type;
        return type !== EventTypes.NAVIGATION_ERROR && type !== EventTypes.NAVIGATION_END;
      })
    });
  } // We may want the start to take in a callback with the router instance as the parameter.
  // This means we can do `.start(router => {/* do stuff with router */})`, as opposed
  // to `.start().then(() => {/* do stuff with router in original scope */})`
  // Public members


  _createClass(Router, [{
    key: 'start',
    value: function start(callback) {
      var _this2 = this;

      return new Promise(function (res, rej) {
        try {
          _this2._scheduler.start(); // Loads initial set of children (running through all middleware).


          _this2._scheduler.dispatch({
            type: EventTypes.CHILDREN_LOADING,
            leaf: {
              node: _this2._store.state.root
            },
            children: _this2._initialChildren
          }); // Look for any next navigation from events and call corresponding method on history.


          _this2._disposers.push(autorun(_this2._handleNextNavigation)); // Initial location.


          _this2._disposers.push(_this2._history.listen(_this2._handleLocationChange));

          res();
        } catch (err) {
          rej(err);
        }
      }).then(function () {
        return delay(0);
      }).then(function () {
        // Schedule initial nextNavigation.
        _this2._scheduler.schedule(asNavigation(_this2._history.location)); // Wait until nextNavigation is processed.


        return _this2._done().then(function () {
          if (_this2._scheduler.event.type === EventTypes.NAVIGATION_ERROR) {
            throw _this2._scheduler.event.error;
          } else {
            callback && callback(_this2);
          }
        });
      }).catch(function (error) {
        _this2.stop();

        throw error;
      });
    }
  }, {
    key: 'stop',
    value: function stop() {
      this._scheduler.stop();

      this._disposers.forEach(function (f) {
        return f();
      });
    }
  }, {
    key: 'subscribeEvent',
    value: function subscribeEvent(f) {
      var _this3 = this;

      return autorun(function () {
        var event = _this3._scheduler.event;

        if (event !== null) {
          f(event);
        }
      });
    }
  }, {
    key: 'createHref',
    value: function createHref(href) {
      return this._history.createHref(asLocation(href));
    }
  }, {
    key: 'push',
    value: function push(href) {
      this._history.push(withSearch(href));

      return this._done();
    }
  }, {
    key: 'replace',
    value: function replace(href) {
      this._history.replace(withSearch(href));

      return this._done();
    }
  }, {
    key: 'updateQuery',
    value: function updateQuery(query) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
        action: 'REPLACE',
        merge: false
      };
      var search = this._store.location.search;
      var existingQuery = search ? querystring.parse(search.substr(1)) : {};
      var updatedQuery = options.merge === true ? _extends({}, existingQuery, query) : query;
      updatedQuery = Object.keys(updatedQuery).reduce(function (acc, k) {
        if (typeof updatedQuery[k] !== 'undefined') {
          acc[k] = updatedQuery[k];
        }

        return acc;
      }, {});
      var queryString = Object.keys(updatedQuery).length > 0 ? '?' + querystring.stringify(updatedQuery) : '';
      var pathname = '' + this.location.pathname + queryString;

      switch (options.action) {
        case 'PUSH':
          return this.push(pathname);

        default:
        case 'REPLACE':
          return this.replace(pathname);
      }
    }
  }, {
    key: 'goBack',
    value: function goBack() {
      this._history.goBack();

      return this._done();
    }
  }, {
    key: 'resolvePath',
    value: function resolvePath(path) {
      var cwd = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.location.pathname;
      var endsWithSlash = path.endsWith('/');
      var segments = path[0] === '/' ? path.split('/') : cwd.split('/').concat(path.split('/'));
      var result = segments.reduce(function (acc, p) {
        if (p && p !== '.') {
          if (p === '..') {
            if (acc.length && acc[acc.length - 1] !== '..') {
              acc.pop();
            }
          } else {
            acc.push(p);
          }
        }

        return acc;
      }, []);
      return '/' + result.join('/') + (endsWithSlash ? '/' : '');
    } // Note: This will not return any dynamic nodes unless they have already loaded.

  }, {
    key: 'getNode',
    value: function getNode(key) {
      return this._store.getNode(key);
    }
  }, {
    key: 'getRoute',
    value: function getRoute(key) {
      return this._store.getRoute(key);
    }
  }, {
    key: 'getParams',
    value: function getParams(key) {
      return this._store.getParams(key);
    }
  }, {
    key: 'getParam',
    value: function getParam(key, paramName) {
      var params = this._store.getParams(key);

      if (params) {
        return params[paramName];
      }
    }
    /* Private helpers */
    // Waits for next navigation event to be processed and resolves.

  }, {
    key: '_done',
    value: function _done() {
      var _this4 = this;

      return new Promise(function (res) {
        when(function () {
          return !_this4.isNavigating;
        }, res);
      });
    }
  }]);

  return Router;
}();

function asLocation(href) {
  return typeof href === 'string' ? {
    pathname: href
  } : _extends({}, href);
}

function asNavigation(location, action) {
  return {
    type: action || 'POP',
    to: _extends({}, location, {
      pathname: location.pathname
    })
  };
}

function withSearch(href) {
  if (typeof href === 'string') {
    return href;
  } else {
    var location = asLocation(href);
    var qs = location.query ? querystring.stringify(location.query) : '';
    return _extends({}, href, {
      search: qs ? '?' + qs : qs
    });
  }
}

export default Router;